// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { useMfaEnrollmentStatus } from '@portal/hooks/useMfaEnrollmentStatus';
import { Button } from '@imprivata-cloud/components';
import { getIdpLookupUrl } from '@utils/utils';
import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { createHrefWithCurrentSearch } from '@utils/navigationSearch';
import { RootRoutes } from '..';
import QuestionIcon from '../assets/icons/question.svg?react';
import SettingsIcon from '../assets/icons/settings.svg?react';
import UserMenu from '../components/dropdown/UserMenu';
import PageHeader from '../components/layout/PageHeader';
import { CUSTOMER_EXPERIENCE_CENTER_URL, ROLE_TYPE_ADMIN } from './constants';
import type { SettingsTab } from './containers/settings/SettingsTabs';
import './PortalLayout.less';

type LandingPageHeaderProps = {
  userLogout: () => void;
  handleSettings?: () => void;
  activeTab?: SettingsTab;
  setActiveTab?: (tab: SettingsTab) => void;
};

export enum TenantType {
  PRIMARY = 'PRIMARY',
  TEST = 'TEST',
}

export enum SelectValues {
  USER_UPN = 'user-upn',
  IMPRIVATA_IDENTITY = 'imprivata-identity',
  LOGOUT = 'Logout',
}

const TRANSLATION_ROOT = 'portal.landing.header';

export const PortalHeader = ({
  userLogout,
  handleSettings,
  isSettingsRoute,
}: LandingPageHeaderProps & { isSettingsRoute?: boolean }) => {
  const { tenantType, userInfo, tenantInfo } = useSession();
  const { t } = useTranslation();
  const SettingsButton = withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Button);
  const [searchParams] = useSearchParams();
  const mfaStatus = useMfaEnrollmentStatus();
  const isMfaPastDeadline = mfaStatus?.isPastDeadline ?? false;

  const headerSelectOptions = useMemo(
    () => [
      {
        key: SelectValues.IMPRIVATA_IDENTITY,
        label: t(`${TRANSLATION_ROOT}.select.imprivata-identity`),
      },
      { key: SelectValues.LOGOUT, label: t(`${TRANSLATION_ROOT}.select.logout`) },
    ],
    [t],
  );

  const handleImprivataIdentity = () => {
    window.open(createHrefWithCurrentSearch(RootRoutes.SELF_SERVICE, searchParams), '_blank');
  };

  const redirectToTestTenant = () => {
    const testTenantId = tenantInfo?.relatedTestTenants[0]?.tenantId;
    if (testTenantId) {
      const idpUrl = getIdpLookupUrl(true, testTenantId);
      window.open(`${idpUrl}`, '_blank');
    }
  };

  const handleSelectChange = (value: string) => {
    switch (value) {
      case SelectValues.IMPRIVATA_IDENTITY:
        handleImprivataIdentity();
        break;
      case SelectValues.LOGOUT:
        userLogout();
        break;
      default:
        break;
    }
  };

  // Help dropdown menu items
  const helpMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'customer-experience-center',
        label: (
          <a href={CUSTOMER_EXPERIENCE_CENTER_URL} target="_blank" rel="noopener noreferrer">
            {t('portal.help.customer-experience-center', 'Customer Experience Center')}
          </a>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <div className="portal-header">
        <PageHeader
          className="content portal-page-header"
          rightSlot={
            <>
              {tenantType === TenantType.PRIMARY && !!tenantInfo?.relatedTestTenants?.length && (
                <Button type={'text'} onClick={redirectToTestTenant}>
                  Go to sandbox environment
                </Button>
              )}
              {tenantType === TenantType.TEST && <p>Sandbox use only</p>}

              {/* Help Dropdown */}
              <Dropdown menu={{ items: helpMenuItems }} trigger={['click']}>
                <Button
                  aria-label={'help-button'}
                  className={'question-button'}
                  icon={<QuestionIcon />}
                  type="text"
                />
              </Dropdown>

              {/* Settings */}
              {!isMfaPastDeadline && (
                <SettingsButton
                  icon={<SettingsIcon fill={'#2c3f57'} />}
                  className={'settings-button'}
                  onClick={handleSettings}
                  aria-label={t('portal.settings.title')}
                />
              )}

              {/* User Menu */}
              <div className="profile-menu">
                <UserMenu
                  menu={{
                    items: headerSelectOptions,
                    onClick: ({ key }) => handleSelectChange(key),
                  }}
                  userUpn={userInfo?.userDisplayName || userInfo?.userUpn}
                  aria-label={'user-menu'}
                />
              </div>
            </>
          }
        />
      </div>

      {!isSettingsRoute && <div className="page-sub-header" />}
    </>
  );
};
